import { getM365AppIdFromPinAppId } from "./getM365AppIdFromPinAppId";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { getAccountScopeUserSettings } from "owa-session-store";
import { isInvalidAppBarPinnedAppsSetting, LOCKED_APPS } from "./isInvalidAppBarPinnedAppsSetting";
import { getIndexerValueForMailboxInfo, type MailboxInfo } from "owa-client-types";
import { logUsage } from "owa-analytics";
import { trace } from "owa-trace";

interface CloudSetting {
    oDataContext: string;
    oDataEtag: string;
    oDataId: string;
    createdDateTime: string;
    id: string;
    itemClass: string;
    lastModifiedDateTime: string;
    name: string;
    parentSetting: string;
    scope: string;
    secondaryKey: string;
    timestamp: number;
    type: string;
    value: string;
}

export type AppBarPinnedAppsValue = {
    lockedApps: string[];
    pinnedApps: string[];
};

const pinnedAppsPerMailboxInfo: Record<string, string> = {};
const lastPinSettingFailure = new Map<string, string>();

/**
 * Gets the user array of pinned app ids. This is a roamed OCS setting between Outlook and OWA.
 *
 * @returns An object containing the locked and pinned app ids
 */

export function getAppBarPinnedAppIdsFromUserSettings(mailboxInfo?: MailboxInfo): AppBarPinnedAppsValue | undefined {
    const mboxInfo = mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    const accountKey = getIndexerValueForMailboxInfo(mboxInfo);
    const settingSource = pinnedAppsPerMailboxInfo[accountKey] ? "Memory" : "PrimeSettings";
    let failureReason: "PrimeSettingsMissing" | "ItemMissing" | "ValueMissingOrEmpty" | "InvalidValue" | undefined;
    //no 'local' pinned apps when first loading, so gets them from global settings
    if (!pinnedAppsPerMailboxInfo[accountKey]) {
        const primeSettings = getAccountScopeUserSettings(mboxInfo)?.PrimeSettings;
        const primeSettingsItems = primeSettings?.Items || [];
        const pinSettingItem = primeSettingsItems.find(item => item?.Id === "AppBarPinnedApps");
        const appBarPinnedAppsCloudSettingValue = pinSettingItem?.Value[0]
            ?.value as CloudSetting["value"];
        failureReason = !primeSettings
            ? "PrimeSettingsMissing"
            : !pinSettingItem
                ? "ItemMissing"
                : !appBarPinnedAppsCloudSettingValue
                    ? "ValueMissingOrEmpty"
                    : undefined;
        
        pinnedAppsPerMailboxInfo[accountKey] = appBarPinnedAppsCloudSettingValue ?? "";
    }
    //'local' pinned apps based on mailbox info
    const updatedPinnedApps = pinnedAppsPerMailboxInfo[accountKey];
    if (!updatedPinnedApps || isInvalidAppBarPinnedAppsSetting(updatedPinnedApps)) {
        const reason = updatedPinnedApps
            ? "InvalidValue"
            : (failureReason ?? "ValueMissingOrEmpty");
        const failure = `${settingSource}:${reason}`;
        if (lastPinSettingFailure.get(accountKey) !== failure) {
            trace.info(`AppBarPinSettingUnavailable: settingSource=${settingSource}, failureReason=${reason}`, "metaos");
            logUsage("AppBarPinSettingUnavailable", mboxInfo, {
                settingSource,
                failureReason: reason,
            });
            lastPinSettingFailure.set(accountKey, failure);
        }
        return undefined;
    }
    lastPinSettingFailure.delete(accountKey);
    // Apps that aren't core apps are prepended with 'App|'
    const appIds = updatedPinnedApps
        .split(",")
        .map((pinSettingAppId: string) => pinSettingAppId.startsWith("App|") ? pinSettingAppId.substring(4) : pinSettingAppId);
    const lockedApps: string[] = [];
    const pinnedApps: string[] = [];
    for (const appId of appIds) {
        if (LOCKED_APPS.includes(appId)) {
            lockedApps.push(getM365AppIdFromPinAppId(appId, mboxInfo));
        }
        else {
            pinnedApps.push(getM365AppIdFromPinAppId(appId, mboxInfo));
        }
    }
    return { lockedApps, pinnedApps };
}

export function setLocalPinnedApps(mailboxInfo: MailboxInfo, newSetting: string): void {
    pinnedAppsPerMailboxInfo[getIndexerValueForMailboxInfo(mailboxInfo)] = newSetting;
}
